import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  SignalIcon,
  SignalSlashIcon,
  ClockIcon,
  GlobeAltIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  ChartBarIcon,
  MapPinIcon,
  UserIcon,
  UserGroupIcon,
  CommandLineIcon,
  ArchiveBoxIcon,
  ShoppingBagIcon,
  CheckBadgeIcon,
  EyeIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import DeviceIcon from '../components/DeviceIcon';
import { format, formatDistanceToNow } from 'date-fns';
import axiosInstance from '../utils/axios';
import { PageHeader, StatCard } from '../components/common';
import { isDM510 } from '../utils/modelLabel';

// Two kinds of dryer are listed here: a DM510 (its record says model '510' and its serial is dm510-<n>; the serial
// still tells when a live-only entry carries no model) and the DM2500i, which is every other device.
const deviceType = (device) =>
  isDM510(device.model) || /^dm510-/i.test(String(device.serial || '')) ? '510' : '2500i';

const ModernDeviceList = ({ devices: liveDevices = [], subscribers = [], users = [] }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('online'); // Changed default from 'all' to 'online'
  const [filterSoldStatus, setFilterSoldStatus] = useState('all'); // all, inventory, sold
  const [filterType, setFilterType] = useState('all'); // all, 510, 2500i
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [dbDevices, setDbDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  // Create a map of active watchers per device
  const getActiveWatchersForDevice = useCallback((deviceSerial) => {
    const watchers = [];

    subscribers.forEach(subscriber => {
      // Check if this subscriber has subscriptions for this device
      const deviceSubscriptions = subscriber.subscriptions?.filter(sub =>
        sub.serial === deviceSerial ||
        sub.topic?.includes(deviceSerial)
      ) || [];

      if (deviceSubscriptions.length > 0) {
        // Find the user details for this subscriber
        const user = users.find(u => u._id === subscriber.userId);
        if (user) {
          watchers.push({
            userId: subscriber.userId,
            name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
            email: user.email,
            role: user.role,
            farmName: user.farmName,
            topics: deviceSubscriptions.map(s => s.topic),
            socketCount: subscriber.socketCount || 1
          });
        }
      }
    });

    return watchers;
  }, [subscribers, users]);

  // Fetch devices from database
  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Single optimized API call that includes all device data
      const response = await axiosInstance.get('/device-details/complete');

      if (response.data.success) {
        setDbDevices(response.data.devices);

      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching devices:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Memoized merged devices to prevent unnecessary recalculations
  const allDevices = useMemo(() => {
    // Merge database devices with live data
    const mergedDevices = dbDevices.map(dbDevice => {
      const liveDevice = liveDevices.find(d => d.serial === dbDevice.serial);
      return {
        ...dbDevice,
        ...liveDevice,
        isFromDb: true,
        isLive: !!liveDevice
      };
    });

    // Add any live devices not in database
    const liveOnlyDevices = liveDevices
      .filter(liveDevice => !dbDevices.find(d => d.serial === liveDevice.serial))
      .map(device => ({ ...device, isFromDb: false, isLive: true }));

    return [...mergedDevices, ...liveOnlyDevices];
  }, [dbDevices, liveDevices]);

  // Memoized helper functions
  // Online means one thing: the cloud has an open link to the dryer right now.
  // The server answers that from its connection registry and the live feed
  // keeps it current, so there is nothing to infer here. A recent timestamp is
  // not evidence of life - for a dryer that has gone away, lastSeen IS the
  // moment it went away, so treating it as recent activity kept every dryer
  // green for five minutes after it lost power.
  const isOnline = useCallback((device) => !!device.isOnline, []);

  const getLastSeenText = useCallback((device) => {
    if (isOnline(device)) {
      // For online devices, show when they connected
      if (device.connectedAt) {
        const connectedDate = new Date(device.connectedAt);
        if (!isNaN(connectedDate.getTime())) {
          const timeAgo = formatDistanceToNow(connectedDate, { addSuffix: true });
          return `Connected ${timeAgo.replace('about ', '')}`;
        }
      }
      return 'Currently active';
    }

    // Try different sources for last seen time
    const lastSeenTime = device.lastSeen || device.lastSessionOffline || device.lastHeartbeat;

    if (lastSeenTime) {
      const lastSeenDate = new Date(lastSeenTime);
      // Check if the date is valid
      if (!isNaN(lastSeenDate.getTime())) {
        const timeAgo = formatDistanceToNow(lastSeenDate, { addSuffix: true });
        // Remove "about" from the string for cleaner display
        return timeAgo.replace('about ', '');
      }
    }

    // Check if device has any connection history
    if (device.totalSessions && device.totalSessions > 0) {
      return 'Previous session ended';
    }

    // Device has never connected or no session history available
    return 'Never connected';
  }, [isOnline]);

  const isDeviceClaimed = useCallback((device) => {
    // Device is claimed ONLY when:
    // - Password has been used (passwordUsed: true means claimed by end user)
    // - OR it has a user associated (userId exists)
    // Note: Being sold to a dealer does NOT mean claimed - dealer must give to end user who claims it
    return device.passwordUsed === true || device.userId;
  }, []);

  // Memoized filtered devices
  const filteredDevices = useMemo(() => {
    return allDevices.filter(device => {
      const matchesSearch = searchTerm === '' ||
                           device.serial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           device.hardwareSerial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           device.smartBoardSerial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           device.hostname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           device.soldBy?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           device.dryerManufacturer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           device.owners?.some(o =>
                             `${o.firstName || ''} ${o.lastName || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             o.farmName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             o.email?.toLowerCase().includes(searchTerm.toLowerCase())
                           ) ||
                           (typeof device.location === 'string' && device.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           device.location?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           device.location?.country?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'online' && isOnline(device)) ||
        (filterStatus === 'offline' && !isOnline(device));

      // Filter by sold status
      const matchesSoldStatus = filterSoldStatus === 'all' ||
        (filterSoldStatus === 'inventory' && device.soldBy?.role === 'inventory') ||
        (filterSoldStatus === 'sold' && device.soldBy?.role !== 'inventory') ||
        (filterSoldStatus === 'demo' && device.demo === true);

      const matchesType = filterType === 'all' || deviceType(device) === filterType;

      return matchesSearch && matchesStatus && matchesSoldStatus && matchesType;
    });
  }, [allDevices, searchTerm, filterStatus, filterSoldStatus, filterType, isOnline]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        subtitle="Monitor and manage your IoT devices"
      />

      {/* Enhanced Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {/* Total Devices */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500">Total Devices</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{allDevices.length}</p>
            </div>
            <DeviceIcon className="h-10 w-10 text-gray-400" />
          </div>
        </motion.div>

        {/* Online Devices with Watchers */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500">Online</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {allDevices.filter(d => isOnline(d)).length}
              </p>
              <div className="mt-1 space-y-0.5">
                <p className="text-xs text-gray-500">
                  {allDevices.length > 0
                    ? `${((allDevices.filter(d => isOnline(d)).length / allDevices.length) * 100).toFixed(0)}% of total`
                    : '0% of total'}
                </p>
                {(() => {
                  // Only count subscribers who have active device subscriptions
                  const activeWatchers = subscribers.filter(s =>
                    s.subscriptions && s.subscriptions.length > 0
                  );
                  return activeWatchers.length > 0 && (
                    <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                      <EyeIcon className="w-3 h-3" />
                      {activeWatchers.length} {activeWatchers.length === 1 ? 'user' : 'users'} watching
                    </p>
                  );
                })()}
              </div>
            </div>
            <SignalIcon className="h-10 w-10 text-green-500" />
          </div>
        </motion.div>

        {/* Offline Devices */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500">Offline</p>
              <p className="text-2xl font-bold text-gray-600 mt-1">
                {allDevices.filter(d => !isOnline(d)).length}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {allDevices.length > 0
                  ? `${((allDevices.filter(d => !isOnline(d)).length / allDevices.length) * 100).toFixed(0)}%`
                  : '0%'}
              </p>
            </div>
            <SignalSlashIcon className="h-10 w-10 text-gray-400" />
          </div>
        </motion.div>

        {/* In Inventory */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500">Inventory</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {dbDevices.filter(d => d.soldBy?.role === 'inventory').length}
              </p>
              <p className="text-xs text-gray-500 mt-1">Ready to sell</p>
            </div>
            <ArchiveBoxIcon className="h-10 w-10 text-blue-500" />
          </div>
        </motion.div>

        {/* Sold Devices */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500">Sold</p>
              <p className="text-2xl font-bold text-indigo-600 mt-1">
                {dbDevices.filter(d => d.soldBy && d.soldBy.role !== 'inventory').length}
              </p>
              <div className="mt-1 space-y-0.5">
                <p className="text-xs text-gray-500">
                  {dbDevices.length > 0
                    ? `${((dbDevices.filter(d => d.soldBy && d.soldBy.role !== 'inventory').length / dbDevices.length) * 100).toFixed(0)}% of total`
                    : '0% of total'}
                </p>
                <p className="text-xs text-gray-600 font-medium">
                  {(() => {
                    const soldDevices = dbDevices.filter(d => d.soldBy && d.soldBy.role !== 'inventory');
                    const byRole = soldDevices.reduce((acc, d) => {
                      const role = d.soldBy?.role || 'unknown';
                      acc[role] = (acc[role] || 0) + 1;
                      return acc;
                    }, {});

                    const roleText = Object.entries(byRole)
                      .map(([role, count]) => {
                        const roleLabel = role === 'direct' ? 'Direct' :
                                        role === 'dealer' ? 'Dealer' :
                                        role === 'oem' ? 'OEM' :
                                        role === 'integrator' ? 'Integrator' : role;
                        return `${count} ${roleLabel}`;
                      })
                      .join(', ');

                    return roleText || 'No sales yet';
                  })()}
                </p>
              </div>
            </div>
            <ShoppingBagIcon className="h-10 w-10 text-indigo-500" />
          </div>
        </motion.div>

        {/* Claimed by Users */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.35 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500">Claimed</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">
                {dbDevices.filter(d => isDeviceClaimed(d)).length}
              </p>
              <div className="mt-1 space-y-0.5">
                <p className="text-xs text-gray-500">
                  {dbDevices.length > 0
                    ? `${((dbDevices.filter(d => isDeviceClaimed(d)).length / dbDevices.length) * 100).toFixed(0)}% of total`
                    : '0% of total'}
                </p>
                <p className="text-xs text-gray-600 font-medium">
                  By end users
                </p>
              </div>
            </div>
            <CheckBadgeIcon className="h-10 w-10 text-purple-500" />
          </div>
        </motion.div>
      </div>

      {/* Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by serial, cabinet serial, owner, farm, dealer, manufacturer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>
          </div>

          {/* Device Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="510">DM510</option>
            <option value="2500i">DM2500i</option>
          </select>

{/* Sold Status Filter */}
          <select
            value={filterSoldStatus}
            onChange={(e) => setFilterSoldStatus(e.target.value)}
            className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Devices</option>
            <option value="inventory">In Inventory</option>
            <option value="sold">Sold</option>
            <option value="demo">Demo Units</option>
          </select>

          {/* Status Filter */}
          <div className="flex gap-2">
            {['all', 'online', 'offline'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  filterStatus === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {/* Refresh */}
          <button 
            onClick={fetchDevices}
            className="p-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all group"
          >
            <ArrowPathIcon className="w-5 h-5 text-gray-700 group-hover:rotate-180 transition-transform duration-500" />
          </button>
        </div>
      </motion.div>

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Loading devices...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h3 className="text-red-800 font-medium">Error loading devices</h3>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
            <button
              onClick={fetchDevices}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Device Grid */}
      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* A filter change takes tiles out and closes the gaps at once. Animated exits, each delayed by the tile's
              place in the list, held every removed tile's cell for up to three seconds. */}
          {filteredDevices.map((device) => {
            const online = isOnline(device);

            return (
              <motion.div
                key={device.serial}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.15 }}
                whileHover={{ y: -2 }}
                className="group"
                onClick={() => navigate(`/dashboard/devices/${device.serial}`)}
              >
                <div className={`relative bg-white rounded-xl transition-all duration-300 cursor-pointer overflow-hidden ${
                  online 
                    ? 'shadow-md border-2 border-green-200 hover:shadow-xl hover:border-green-300' 
                    : 'shadow-sm border border-gray-200 hover:shadow-lg hover:border-blue-300'
                }`}>
                  {/* Header Section */}
                  <div className={`p-4 border-b ${
                    online 
                      ? 'bg-gradient-to-r from-green-50 to-white border-green-100' 
                      : 'bg-gradient-to-r from-gray-50 to-white border-gray-100'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          online ? 'bg-green-100' : 'bg-gray-100'
                        }`}>
                          <DeviceIcon model={device.model} className={`w-6 h-6 ${
                            online ? 'text-green-600' : 'text-gray-500'
                          }`} />
                        </div>
                        <div>
                          {/* Cabinet serial leads the card: it is the number
                              the CRM records at shipping, so it is what
                              anyone looking for a particular box searches by.
                              Shown even when blank, so a box that never had
                              its serials entered is obvious at a glance. */}
                          <p className="mb-1 flex items-center gap-1 text-sm">
                            <span className="text-xs text-gray-400">Cabinet</span>
                            {device.hardwareSerial ? (
                              <span
                                className="truncate max-w-[140px] font-mono font-medium text-blue-700"
                                title={device.hardwareSerial}
                              >
                                {device.hardwareSerial}
                              </span>
                            ) : (
                              <span className="text-xs italic text-gray-400">not set</span>
                            )}
                          </p>
                          <h3 className="font-semibold text-gray-900">{device.serial}</h3>
                          {device.smartBoardSerial && (
                            <p className="flex items-center gap-1 text-xs font-mono text-gray-500">
                              <span className="text-gray-400">Board</span>
                              <span>{device.smartBoardSerial}</span>
                            </p>
                          )}
                          <p className="text-sm text-gray-600">{device.model || 'Unknown Model'}</p>
                        </div>
                      </div>
                      {/* Status Badge - Inventory/Claimed/Unclaimed, plus DEMO
                          for a unit on loan */}
                      <div className="flex items-center gap-1.5">
                        {device.demo && (
                          <span className="text-xs px-2.5 py-1 rounded-full font-semibold bg-purple-100 text-purple-700">
                            DEMO
                          </span>
                        )}
                        {device.soldBy?.role === 'inventory' ? (
                          <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-blue-100 text-blue-700">
                            In Inventory
                          </span>
                        ) : (
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                            isDeviceClaimed(device)
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {isDeviceClaimed(device) ? 'Claimed' : 'Unclaimed'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Info Section */}
                  <div className="p-4 space-y-3">
                    {/* Linked Users Section - Shows all owners with online status */}
                    {device.owners && device.owners.length > 0 && (() => {
                      const watchers = getActiveWatchersForDevice(device.serial);
                      const watcherUserIds = watchers.map(w => w.userId);

                      return (
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-3 border border-indigo-100">
                          <div className="flex items-center gap-2 mb-2">
                            <UserGroupIcon className="w-4 h-4 text-indigo-500" />
                            <span className="text-sm font-medium text-indigo-700">Linked {device.owners.length === 1 ? 'User' : 'Users'}</span>
                          </div>
                          <div className="space-y-1.5">
                            {device.owners.map((owner, idx) => {
                              const isWatching = watcherUserIds.includes(owner._id);
                              return (
                                <div key={owner._id || idx} className="flex items-center gap-2 group/user">
                                  {/* Online/Offline indicator */}
                                  <div className="relative flex-shrink-0">
                                    {isWatching ? (
                                      <>
                                        <div className="absolute inset-0 w-2 h-2 bg-green-400 rounded-full animate-ping opacity-75"></div>
                                        <div className="relative w-2 h-2 bg-green-500 rounded-full"></div>
                                      </>
                                    ) : (
                                      <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                                    )}
                                  </div>

                                  {/* User info */}
                                  <div className="flex items-center gap-1.5 min-w-0 flex-1 flex-wrap">
                                    <span className={`text-sm font-medium ${isWatching ? 'text-gray-900' : 'text-gray-600'}`}>
                                      {owner.firstName}
                                    </span>

                                    {/* Farm name */}
                                    {owner.farmName && (
                                      <span className="text-xs text-gray-500">
                                        ({owner.farmName})
                                      </span>
                                    )}

                                    {/* Nickname if exists */}
                                    {owner.nickname && (
                                      <span className="text-xs text-purple-600 font-medium">
                                        → "{owner.nickname}"
                                      </span>
                                    )}
                                  </div>

                                  {/* Watching badge */}
                                  {isWatching && (
                                    <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full flex-shrink-0">
                                      <EyeIcon className="w-3 h-3" />
                                      <span className="hidden sm:inline">watching</span>
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {device.soldBy && device.soldBy.role !== 'inventory' && (
                      <div className="flex items-center gap-2">
                        <UserIcon className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium text-blue-700">
                          Sold by {device.soldBy.name}
                        </span>
                      </div>
                    )}

                    {!isDeviceClaimed(device) && (
                      <div className="flex items-center gap-2">
                        <InformationCircleIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600 italic">
                          Device not registered to any user
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Footer with Online Status */}
                  <div className="px-4 py-3 border-t border-gray-100 mt-auto">
                    <div className="flex items-center justify-between">
                      {/* Status - Left */}
                      <div className="flex items-center gap-2">
                        {online ? (
                          <SignalIcon className="w-4 h-4 text-green-600" />
                        ) : (
                          <SignalIcon className="w-4 h-4 text-gray-400" />
                        )}
                        <div className="flex flex-col">
                          <span className={`text-sm font-medium ${
                            online ? 'text-green-600' : 'text-gray-600'
                          }`}>
                            {online ? 'Online' : 'Offline'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {getLastSeenText(device)}
                          </span>
                        </div>
                      </div>
                      {/* Right side - Location & Details */}
                      <div className="flex flex-col items-end gap-1">
                        {device.location && (
                          <div className="flex items-center gap-2 text-gray-500">
                            <MapPinIcon className="w-4 h-4" />
                            <span className="text-sm">
                              {typeof device.location === 'string'
                                ? device.location
                                : device.location.formatted ||
                                  (device.location.city && device.location.country
                                    ? `${device.location.city}, ${device.location.country}`
                                    : device.location.city || 'Location')}
                            </span>
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/devices/${device.serial}`);
                          }}
                          className="text-sm font-medium text-blue-600 hover:text-blue-700"
                        >
                          Details →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {filteredDevices.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <DeviceIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-700 text-lg">No devices found</p>
          <p className="text-gray-500 text-sm mt-2">
            {searchTerm || filterStatus !== 'all' || filterType !== 'all' || filterSoldStatus !== 'all'
              ? 'Try adjusting your filters' 
              : 'Connect a device to get started'}
          </p>
        </motion.div>
      )}

    </div>
  );
};

export default ModernDeviceList;